/* eslint-disable indent */
import { useDispatch, useSelector } from 'react-redux'
import { useState, useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import axios from 'axios'
import { initializeUser } from '../reducers/userReducer'
import { fetchPlayer } from '../reducers/playerReducer'
import { initializeCandidates } from '../reducers/candidateReducer'
import TrainButton from './TrainButton'
import TrainingHistory from './TrainingHistory'
import ScoutSkills from './ScoutSkills'
import Skills from './Skills'
import { Card } from 'react-bootstrap'
import { getTranslation } from '../services/translations'

const Player = () => {
  const id = useParams().id
  const location = useLocation()
  const user = useSelector(state => state.user)
  const cachedPlayer = useSelector(state => state.players.playerCache?.[id])
  const lang = (user && user.language) || 'fi'
  const t = key => getTranslation(lang, key)
  const dispatch = useDispatch()
  const [player, setPlayer] = useState(cachedPlayer || null)
  const [message, setMessage] = useState('')
  const [candidates, setCandidates] = useState([])
  const [trainingPlan, setTrainingPlan] = useState([])
  const [playerSkills, setPlayerSkills] = useState({})
  const [hasScoutAccess, setHasScoutAccess] = useState(false)
  const [nation, setNation] = useState(null)
  const [ownerNation, setOwnerNation] = useState(null)
  const [exportedTargetSkills, setExportedTargetSkills] = useState(null)
  const [exportedOptimizedWeeks, setExportedOptimizedWeeks] = useState(null)

  useEffect(() => {
    const loadData = async () => {
      // Varmista että käyttäjä on ladattu Reduxiin
      const userData = await dispatch(initializeUser())
      if (!userData) return

      const token = userData.oauth_token || userData.id
      // Hae pelaaja: ensin cachesta, sitten backendistä
      let fetchedPlayer = cachedPlayer
      if (!fetchedPlayer) {
        fetchedPlayer = await dispatch(fetchPlayer(id, token))
      }

      if (fetchedPlayer) {
        setPlayer(fetchedPlayer)
        setPlayerSkills(fetchedPlayer.playerSkills)
        setTrainingPlan(fetchedPlayer.scoutTrainingPlan)
        // Hae ehdokaslistat Reduxista (tai backendistä jos ei cachessa)
        const candidatesList = await dispatch(initializeCandidates(token))
        setCandidates(candidatesList)
      } else {
        setMessage(t('noAccessToPlayer'))
      }
    }
    loadData()
  }, [id])

  // Käsittele palautetut tavoitetaidot SkillDistribution-sivulta
  useEffect(() => {
    if (location.state && location.state.exportedTargetSkills) {
      setExportedTargetSkills(location.state.exportedTargetSkills)
      setExportedOptimizedWeeks(location.state.optimizedWeeks || null)
      // Tyhjennä navigaation state välttääksemme uudelleenasetukset
      window.history.replaceState({}, document.title)
    }
  }, [location.state])

  // Tarkista Scout-oikeudet kun user ja player ovat ladattu
  useEffect(() => {
    const checkScoutAccess = async () => {
      if (player?.isCustom) {
        setHasScoutAccess(true)
        return
      }

      if (player && user && user.staff) {
        try {
          if (player.nativeLeagueId && !isNaN(player.nativeLeagueId)) {
            const nationResponse = await axios.get(
              `/api/nations/${player.nativeLeagueId}`
            )
            if (nationResponse.data) {
              setNation(nationResponse.data)
              if (nationResponse.data.NationalTeamId) {
                const playerNationalTeamId = nationResponse.data.NationalTeamId

                // Tarkista onko käyttäjällä Staff-rooli tälle maalle
                if (
                  user.staff.some(
                    s => s.nationalTeamId === playerNationalTeamId
                  )
                ) {
                  setHasScoutAccess(true)
                } else if (
                  user.NationalTeams &&
                  user.NationalTeams.NationalTeam
                ) {
                  const ntArray = Array.isArray(user.NationalTeams.NationalTeam)
                    ? user.NationalTeams.NationalTeam
                    : [user.NationalTeams.NationalTeam]
                  if (
                    ntArray.some(
                      nt => nt.NationalTeamID === playerNationalTeamId
                    )
                  ) {
                    setHasScoutAccess(true)
                  }
                }
              }
            }
          }
        } catch (err) {
          console.error('Error checking scout access:', err)
        }
      } else if (player && !nation) {
        // Hae nation-data myös ei-staff käyttäjille lipun näyttämiseksi
        try {
          if (player.nativeLeagueId && !isNaN(player.nativeLeagueId)) {
            const nationResponse = await axios.get(
              `/api/nations/${player.nativeLeagueId}`
            )
            if (nationResponse.data) {
              setNation(nationResponse.data)
            }
          }
        } catch (err) {
          console.error('Error fetching nation:', err)
        }
      }
    }
    checkScoutAccess()
  }, [player, user])

  // Hae omistajan maan tiedot lipulle
  useEffect(() => {
    const fetchOwnerNation = async () => {
      if (player?.owningTeam?.leagueId && !isNaN(player.owningTeam.leagueId)) {
        try {
          const res = await axios.get(
            `/api/nations/${player.owningTeam.leagueId}`
          )
          if (res.data) {
            setOwnerNation(res.data)
          }
        } catch (err) {
          console.error('Error fetching owner nation:', err)
        }
      }
    }
    fetchOwnerNation()
  }, [player])

  const addMessage = message => {
    setMessage(message)
    setTimeout(() => {
      setMessage('')
    }, 5000)
  }

  const calculateSkillChange = (currentSkill, oldSkill) => {
    if (oldSkill === undefined) return ''
    const difference = currentSkill - oldSkill
    if (difference > 0) {
      return `+${difference.toFixed(3)}`
    } else if (difference < 0) {
      return `${difference.toFixed(3)}`
    } else {
      return ''
    }
  }

  const isOwner = Boolean(
    player?.owningTeam?.teamId &&
      user?.teams?.some(
        team => Number(team.teamID) === Number(player.owningTeam.teamId)
      )
  )
  const showScoutView = hasScoutAccess || isOwner || Boolean(player?.isCustom)

  //console.log(user)

  return player ? (
    <>
      {/* Käyttäjän oikeuksien tarkistus pelaajan maan perusteella */}
      {/* Odotetaan että user.staff on määritelty ennen kuin näytetään ei-Staff-näkymä */}
      {user && user.staff === undefined ? (
        <Card
          className="flex-fill"
          style={{
            backgroundColor: '#2C2F33',
            color: '#FFFFFF',
            borderColor: '#1E2124',
          }}
        >
          <Card.Header
            as="h5"
            style={{ backgroundColor: '#23272A', color: '#FFFFFF' }}
          >
            Loading...
          </Card.Header>
        </Card>
      ) : showScoutView ? (
        <ScoutSkills
          player={player}
          playerSkills={playerSkills}
          setPlayerSkills={setPlayerSkills}
          calculateSkillChange={calculateSkillChange}
          message={message}
          addMessage={addMessage}
          candidates={candidates}
          trainingPlan={trainingPlan}
          setTrainingPlan={setTrainingPlan}
          exportedTargetSkills={exportedTargetSkills}
          exportedOptimizedWeeks={exportedOptimizedWeeks}
          nation={nation}
          ownerNation={ownerNation}
        />
      ) : (
        <Skills
          player={player}
          playerSkills={playerSkills}
          calculateSkillChange={calculateSkillChange}
          trainingPlan={trainingPlan}
          nation={nation}
          ownerNation={ownerNation}
        />
      )}
      <TrainingHistory player={player} />
      {!showScoutView && <TrainButton player={player} lang={lang} />}
    </>
  ) : (
    <Card
      className="flex-fill"
      style={{
        backgroundColor: '#2C2F33',
        color: '#FFFFFF',
        borderColor: '#1E2124',
      }}
    >
      <Card.Header
        as="h5"
        style={{ backgroundColor: '#23272A', color: '#FFFFFF' }}
      >
        {message}
      </Card.Header>
    </Card>
  )
}

export default Player
